import { useResumeGetCertsAndSkills, useSearchGetSearchQueries } from "@crowdcoursing/api/openapi";
import { keepPreviousData } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";

import { type DateRangeValue } from "~ui/atoms/Form/DateRange/PopoverDateRangePicker";
import { TalentAlignmentDashboard } from "~ui/templates/AdminTemplate/DashboardStats/TalentAlignmentDashboard";

const START_DATE_STORAGE_KEY = "talentAlignmentTab.startDate";

const getInitialTimeRange = (): DateRangeValue => {
  try {
    const stored = localStorage.getItem(START_DATE_STORAGE_KEY);
    if (stored) {
      const startDate = DateTime.fromISO(stored);
      if (startDate?.isValid) {
        return { endDate: DateTime.now(), startDate };
      }
    }
  } catch {
    // Fall through to default
  }
  return {
    endDate: DateTime.now(),
    startDate: DateTime.now().minus({ weeks: 1 }),
  };
};

const useTalentAlignmentProps = () => {
  const [timeRange, setTimeRange] = useState<DateRangeValue>(getInitialTimeRange);

  const handleTimeRangeChange = (newTimeRange: DateRangeValue) => {
    setTimeRange(newTimeRange);
    if (newTimeRange.startDate) {
      localStorage.setItem(START_DATE_STORAGE_KEY, newTimeRange.startDate.toISO() ?? "");
    }
  };

  const searchQueryParams = useMemo(
    () => ({
      end: timeRange.endDate?.toISO() ?? DateTime.now().toISO(),
      start: timeRange.startDate?.toISO() ?? DateTime.now().minus({ weeks: 1 }).toISO(),
    }),
    [timeRange],
  );

  // Fetch search queries data
  const { data: searchData, status: searchStatus } = useSearchGetSearchQueries(
    { queryParams: searchQueryParams },
    { placeholderData: keepPreviousData },
  );

  // Fetch skills/certifications data
  const { data: skillsData, status: skillsStatus } = useResumeGetCertsAndSkills({});

  return {
    certifications: skillsData?.certifications ?? null,
    onTimeRangeChange: handleTimeRangeChange,
    searchData: searchData ?? [],
    searchStatus,
    skills: skillsData?.skills ?? null,
    skillsStatus,
    timeRange,
  };
};

export const TalentAlignmentTab = () => {
  const props = useTalentAlignmentProps();
  return <TalentAlignmentDashboard {...props} />;
};
